"use client"

import React, { useState, useEffect, Suspense } from 'react';
import { Loader2 } from 'lucide-react';

// Type definitions for CodeMirror props
export interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language?: 'json' | 'sql';
  theme?: 'light' | 'dark';
  placeholder?: string;
  height?: string;
  minHeight?: string;
  className?: string;
  basicSetup?: {
    lineNumbers?: boolean;
    foldGutter?: boolean;
    dropCursor?: boolean;
    allowMultipleSelections?: boolean;
    searchKeymap?: boolean;
  };
  style?: React.CSSProperties;
}

// Loading fallback component
const CodeEditorSkeleton = ({ height = '300px', minHeight = '100px' }: { height?: string; minHeight?: string }) => (
  <div 
    className="border border-border rounded-md bg-muted/50 flex items-center justify-center"
    style={{ height, minHeight }}
  >
    <div className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span className="text-sm">Loading editor...</span>
    </div>
  </div>
);

// Dynamic CodeMirror component with lazy loading
export const CodeEditor = React.memo((props: CodeEditorProps) => {
  const [CodeMirrorComponent, setCodeMirrorComponent] = useState<React.ComponentType<any> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Add cursor styles to fix cursor visibility
  useEffect(() => {
    const cursorStyles = `
      .cm-editor .cm-cursor, 
      .cm-editor .cm-cursor-primary,
      .cm-editor .cm-dropCursor {
        border-left-color: ${isDarkMode ? '#ffffff !important' : '#000000 !important'};
        border-left-width: 2px !important;
        border-left-style: solid !important;
        opacity: 1 !important;
      }
      .cm-editor .cm-cursor-secondary {
        border-left-color: ${isDarkMode ? 'rgba(255, 255, 255, 0.6) !important' : 'rgba(0, 0, 0, 0.6) !important'};
        border-left-width: 2px !important;
        border-left-style: solid !important;
      }
      .cm-editor.cm-focused .cm-cursor,
      .cm-editor.cm-focused .cm-cursor-primary {
        border-left-color: ${isDarkMode ? '#ffffff !important' : '#000000 !important'};
        animation: cm-blink 1.2s infinite;
      }
      @keyframes cm-blink {
        0% { opacity: 1; }
        50% { opacity: 0; }
        100% { opacity: 1; }
      }
    `;

    let styleElement = document.getElementById('dynamic-codemirror-cursor-styles');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'dynamic-codemirror-cursor-styles';
      document.head.appendChild(styleElement);
    }
    styleElement.textContent = cursorStyles;

    return () => {
      const element = document.getElementById('dynamic-codemirror-cursor-styles');
      if (element) {
        element.remove();
      }
    };
  }, [isDarkMode]);

  useEffect(() => {
    let mounted = true;

    const loadCodeMirror = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Dynamically import CodeMirror and required extensions
        const [
          { default: CodeMirror },
          { json },
          { sql },
          { oneDark }
        ] = await Promise.all([
          import('@uiw/react-codemirror'),
          import('@codemirror/lang-json'),
          import('@codemirror/lang-sql'),
          import('@codemirror/theme-one-dark')
        ]);

        if (!mounted) return;

        // Create a wrapper component that applies the correct language extension
        const CodeMirrorWrapper = React.forwardRef<any, CodeEditorProps>((componentProps, ref) => {
          const { language = 'json', theme, ...restProps } = componentProps;
          
          // Determine extensions based on language
          const extensions = [];
          if (language === 'json') {
            extensions.push(json());
          } else if (language === 'sql') {
            extensions.push(sql());
          }

          // Apply theme - use prop theme if provided, otherwise auto-detect
          const effectiveTheme = theme || (isDarkMode ? 'dark' : 'light');
          const codeMirrorTheme = effectiveTheme === 'dark' ? oneDark : 'light';

          return (
            <CodeMirror
              {...restProps}
              ref={ref}
              theme={codeMirrorTheme}
              extensions={extensions}
            />
          );
        });

        CodeMirrorWrapper.displayName = 'CodeMirrorWrapper';

        setCodeMirrorComponent(() => CodeMirrorWrapper);
      } catch (err) {
        console.error('Failed to load CodeMirror:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load code editor');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadCodeMirror();

    return () => {
      mounted = false;
    };
  }, []);

  // Show loading state
  if (isLoading) {
    return <CodeEditorSkeleton height={props.height} minHeight={props.minHeight} />;
  }

  // Show error state
  if (error) {
    return (
      <div 
        className="border border-destructive rounded-md bg-destructive/10 flex items-center justify-center"
        style={{ height: props.height || '300px', minHeight: props.minHeight || '100px' }}
      >
        <div className="text-center text-destructive px-4">
          <p className="text-sm font-medium">Failed to load code editor</p>
          <p className="text-xs mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Render the loaded CodeMirror component
  if (CodeMirrorComponent) {
    return <CodeMirrorComponent {...props} />;
  }

  // Fallback (should not reach here)
  return <CodeEditorSkeleton height={props.height} minHeight={props.minHeight} />;
});

CodeEditor.displayName = 'CodeEditor';

// Export a default that includes Suspense boundary
export default function CodeEditorWithSuspense(props: CodeEditorProps) {
  return (
    <Suspense fallback={<CodeEditorSkeleton height={props.height} minHeight={props.minHeight} />}>
      <CodeEditor {...props} />
    </Suspense>
  );
}
